import { Component, OnInit, ViewChild, AfterViewInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { ApiService } from 'src/app/services';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute } from '@angular/router';
import { CareerPlanYearsListDialogComponent } from './career-plan-years-list-dialog/career-plan-years-list-dialog.component';
import { WorkingContractCareerPlan } from 'src/app/models/working_contract_career_plan.model';
import dayjs from 'dayjs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { SelectCareerPlanTemplateDialogComponent } from './select-career-plan-template-dialog/select-career-plan-template-dialog.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  DatePickerDialogComponent,
  DatePickerDialogData,
} from 'src/app/components/main/shared/date-picker-dialog/date-picker-dialog.component';

@Component({
  selector: 'app-career-plans-list',
  templateUrl: './career-plans-list.component.html',
  styleUrls: ['./career-plans-list.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTableModule,
    MatPaginatorModule,
    MatIconModule,
    MatTooltipModule,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatDatepickerModule,
  ],
})
export class CareerPlansListComponent implements OnInit, AfterViewInit {
  private dialog = inject(MatDialog);
  private api = inject(ApiService);
  private activatedRoute = inject(ActivatedRoute);
  private fb = inject(FormBuilder);

  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  user_id: number;
  displayedColumns: string[] = ['current_from', 'current_until', 'administrators_annotations', 'actions'];
  dataSource = new MatTableDataSource<WorkingContractCareerPlanCustom>([]);

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  cloneForm: FormGroup;
  minCloneDate: Date = dayjs().add(1, 'day').startOf('day').toDate();

  constructor() {
    this.cloneForm = this.fb.group({
      date: [this.minCloneDate, Validators.required],
    });
  }

  ngOnInit(): void {
    this.user_id = parseInt(this.activatedRoute.parent?.snapshot.paramMap.get('id') as string);
    this.fetchAllCareerPlans();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  openCareerPlanYearsListDialog(workingContractCareerPlan: WorkingContractCareerPlanCustom): void {
    this.dialog.open(CareerPlanYearsListDialogComponent, {
      data: { workingContractCareerPlan },
      width: '1200px',
    });
  }

  cloneCareerPlan(working_contract_career_plan_id: number): void {
    const data: DatePickerDialogData = {
      dialog_title: 'Clonar plan de carrera',
      dialog_ok_button: 'Clonar',
      label: 'Fecha de inicio del nuevo plan',
      selected_date: null,
      min_date: this.minCloneDate,
      filter: null,
    };

    this.dialog
      .open(DatePickerDialogComponent, {
        width: '400px',
        data,
      })
      .afterClosed()
      .subscribe((date: Date): void => {
        this.api.cloneWorkingContractCareerPlan(working_contract_career_plan_id, date).subscribe((): void => {
          this.fetchAllCareerPlans();
        });
      });
  }

  updateDates(workingContractCareerPlan: WorkingContractCareerPlanCustom): void {
    const body = {
      current_from: dayjs(workingContractCareerPlan.current_from).format('YYYY-MM-DD'),
      current_until: workingContractCareerPlan.current_until ? dayjs(workingContractCareerPlan.current_until).format('YYYY-MM-DD') : null,
      administrators_annotations: workingContractCareerPlan.administrators_annotations,
    };

    this.api.updateWorkingContractCareerPlan(workingContractCareerPlan.id, body).subscribe((): void => {
      this.fetchAllCareerPlans();
    });
  }

  deleteCareerPlan(plan: WorkingContractCareerPlanCustom): void {
    if (!confirm('¿Seguro que quieres eliminar este plan de carrera?')) return;

    this.api.deleteWorkingContractCareerPlan(plan.id).subscribe((): void => {
      this.fetchAllCareerPlans();
    });
  }

  openSelectTemplateDialog(): void {
    this.dialog
      .open(SelectCareerPlanTemplateDialogComponent)
      .afterClosed()
      .subscribe((data): void => {
        if (undefined === data) return;

        const body = {
          working_contract_template_career_plan_template_id: data.templateId,
          current_from: data.current_from,
        };

        this.api.createWorkingContractCareerPlanFromTemplate(this.user_id, body).subscribe((): void => {
          this.fetchAllCareerPlans();
        });
      });
  }

  private fetchAllCareerPlans(): void {
    this.api.getUserWorkingContractCareerPlans(this.user_id).subscribe((data): void => {
      this.dataSource.data = data.map((plan: WorkingContractCareerPlan, idx, arr): WorkingContractCareerPlanCustom => {
        const previous = arr[idx - 1];
        const next = arr[idx + 1];

        const maxFrom = plan.current_until ? dayjs(plan.current_until).toDate() : null;
        const minFrom = previous?.current_until ? dayjs(previous.current_until).add(1, 'day').toDate() : null;
        const minUntil = plan.current_from ? dayjs(plan.current_from).toDate() : null;
        const maxUntil = next?.current_from ? dayjs(next.current_from).subtract(1, 'day').toDate() : null;

        return { ...plan, minFrom, maxFrom, minUntil, maxUntil };
      });
    });
  }
}

interface WorkingContractCareerPlanCustom extends WorkingContractCareerPlan {
  minFrom: Date | null;
  maxFrom: Date | null;
  minUntil: Date | null;
  maxUntil: Date | null;
}
